import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { IconButton } from '../design/primitives/Button';
import { useEscape } from '../design/primitives/Overlay';

// Inspector: the dockable right-hand panel that replaces modal soup. Any screen can
// `show({ title, content })`; the panel resizes by dragging its left edge, pins so
// navigation doesn't close it, and closes on Esc when unpinned.

export interface InspectorContent {
  key?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  content: ReactNode;
  actions?: ReactNode;
  /** Called when the panel closes (user or navigation). */
  onClose?: () => void;
}

interface InspectorState {
  current: InspectorContent | null;
  pinned: boolean;
  width: number;
  show: (c: InspectorContent) => void;
  close: () => void;
  setPinned: (p: boolean) => void;
  setWidth: (w: number) => void;
}

const Ctx = createContext<InspectorState | null>(null);

export function InspectorProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<InspectorContent | null>(null);
  const [pinned, setPinned] = useState(false);
  const [width, setWidthState] = useState<number>(() => {
    try {
      return Number(localStorage.getItem('hf.inspector.w')) || 380;
    } catch {
      return 380;
    }
  });
  const setWidth = useCallback((w: number) => {
    const c = Math.max(280, Math.min(720, w));
    setWidthState(c);
    try {
      localStorage.setItem('hf.inspector.w', String(c));
    } catch {
      /* ignore */
    }
  }, []);
  const close = useCallback(() => {
    setCurrent((c) => {
      c?.onClose?.();
      return null;
    });
  }, []);
  const show = useCallback((c: InspectorContent) => setCurrent(c), []);
  const value = useMemo(() => ({ current, pinned, width, show, close, setPinned, setWidth }), [current, pinned, width, show, close, setWidth]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useInspector(): InspectorState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useInspector requires <InspectorProvider>');
  return c;
}

/** Closes the (unpinned) inspector when `dep` (typically the route) changes. */
export function useInspectorRouteReset(dep: unknown) {
  const { pinned, close, current } = useInspector();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!pinned && current) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
}

/** The dock itself — rendered by AppShell beside the content column. */
export function InspectorDock() {
  const { current, pinned, width, close, setPinned, setWidth } = useInspector();
  const dragging = useRef(false);
  const ref = useRef<HTMLElement>(null);
  useEscape(Boolean(current) && !pinned, close);
  useEffect(() => {
    if (current) ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [current?.key, current]);
  if (!current) return null;
  return (
    <aside ref={ref} className={['hf-inspector', pinned && 'pinned'].filter(Boolean).join(' ')} style={{ width }} aria-label="Inspector" role="complementary">
      <div
        className="hf-inspector-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize inspector"
        aria-valuenow={width}
        tabIndex={0}
        onPointerDown={(e) => {
          dragging.current = true;
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!dragging.current) return;
          setWidth(window.innerWidth - e.clientX);
        }}
        onPointerUp={() => (dragging.current = false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setWidth(width + 20);
          if (e.key === 'ArrowRight') setWidth(width - 20);
        }}
        onDoubleClick={() => setWidth(380)}
      />
      <header className="hf-inspector-head">
        <div className="hf-grow" style={{ minWidth: 0 }}>
          <h2 className="hf-display" style={{ fontSize: 'var(--t-18)' }}>
            {current.title}
          </h2>
          {current.subtitle && <div className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>{current.subtitle}</div>}
        </div>
        {current.actions}
        <IconButton icon={pinned ? 'pin-off' : 'pin'} label={pinned ? 'Unpin' : 'Pin'} size="sm" aria-pressed={pinned} onClick={() => setPinned(!pinned)} />
        <IconButton icon="x" label="Close inspector" size="sm" onClick={close} />
      </header>
      <div className="hf-inspector-body hf-scroll">{current.content}</div>
    </aside>
  );
}
